import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { animate, stagger } from 'animejs';
import { message } from 'antd';
import { ThunderboltOutlined, SafetyOutlined, ApiOutlined } from '@ant-design/icons';
import { authLogin, authGetUserPermissions, type PermissionInfo } from '@/services/auth';
import { useUserStore } from '@/stores/userStore';
import styles from './Login.module.css';

interface LoginForm {
  username: string;
  password: string;
}

function CockpitLogin() {
  const navigate = useNavigate();
  const { login } = useUserStore();
  const loginBoxRef = useRef<HTMLDivElement>(null);
  const starfieldRef = useRef<HTMLDivElement>(null);
  const symbolsRef = useRef<HTMLDivElement>(null);
  const starCalendarRef = useRef<HTMLDivElement>(null);
  const particlesRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState<LoginForm>({
    username: '',
    password: '',
  });
  const [starTime, setStarTime] = useState('');
  const [loading, setLoading] = useState(false);

  // 伊敦星历计时器
  useEffect(() => {
    const startDate = new Date('2022-06-30T00:00:00');

    const updateStarTime = () => {
      const now = new Date();
      const diff = now.getTime() - startDate.getTime();
      const years = (diff / (1000 * 60 * 60 * 24 * 365.25)).toFixed(8);

      setStarTime(`伊敦星历 ${years}年`);
    };

    updateStarTime();
    const timer = setInterval(updateStarTime, 1000);

    return () => clearInterval(timer);
  }, []);

  // 鼠标移动粒子效果
  useEffect(() => {
    if (!particlesRef.current) return;

    const particlesContainer = particlesRef.current;
    let lastTime = 0;
    const throttleDelay = 20; // 降低节流延迟，粒子更密集

    const handleMouseMove = (e: MouseEvent) => {
      const now = Date.now();
      if (now - lastTime < throttleDelay) return;
      lastTime = now;

      // 每次生成2-3个粒子
      const particleCount = 2 + Math.floor(Math.random() * 2);

      for (let i = 0; i < particleCount; i++) {
        const particle = document.createElement('div');
        particle.className = styles.particle;

        // 在鼠标位置附近随机偏移
        const offsetX = (Math.random() - 0.5) * 10;
        const offsetY = (Math.random() - 0.5) * 10;
        particle.style.left = `${e.clientX + offsetX}px`;
        particle.style.top = `${e.clientY + offsetY}px`;

        const size = 4 + Math.random() * 6; // 增大粒子尺寸
        particle.style.width = `${size}px`;
        particle.style.height = `${size}px`;

        // 设置随机扩散方向
        particle.style.setProperty('--random-x', Math.random().toString());
        particle.style.setProperty('--random-y', Math.random().toString());

        particlesContainer.appendChild(particle);

        // 1200ms后移除粒子
        setTimeout(() => {
          particle.remove();
        }, 1200);
      }
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  // 创建星空背景
  useEffect(() => {
    if (!starfieldRef.current) return;

    const starfield = starfieldRef.current;
    const starCount = 200;

    for (let i = 0; i < starCount; i++) {
      const star = document.createElement('div');
      star.className = styles.star;
      const size = Math.random() * 3;
      star.style.width = `${size}px`;
      star.style.height = `${size}px`;
      star.style.left = `${Math.random() * 100}%`;
      star.style.top = `${Math.random() * 100}%`;
      star.style.animationDelay = `${Math.random() * 3}s`;
      starfield.appendChild(star);
    }

    return () => {
      starfield.innerHTML = '';
    };
  }, []);

  // 创建数学物理符号和AI量化术语动画 - 轮流闪烁
  useEffect(() => {
    if (!symbolsRef.current) return;

    const symbolsContainer = symbolsRef.current;
    const symbols = [
      // 数学物理符号（精简）
      'α', 'β', 'γ', 'θ', 'λ', 'π', 'σ', 'φ',
      '∑', '∫', '∞',
      // AI和量化领域术语
      'Aden', 'Sharpe', 'LSTM', 'Transformer', 'GAN', 'CNN',
      'BERT', 'GPT', 'Alpha', 'Beta', 'Neural', 'Quantum',
      'Gradient', 'Attention', 'ReLU', 'SGD', 'XGBoost',
      'VAE', 'DQN', 'Policy', 'Reward'
    ];

    let symbolIndex = 0;

    // 定时创建符号，轮流出现
    const createSymbol = () => {
      const symbol = document.createElement('div');
      symbol.className = styles.mathSymbol;

      // 轮流使用不同的符号
      const currentSymbol = symbols[symbolIndex % symbols.length];
      symbol.textContent = currentSymbol;
      symbolIndex++;

      // 根据文字长度动态调整字体大小 - 词汇长的文字小一点
      const textLength = currentSymbol.length;
      let baseSize;
      if (textLength <= 3) {
        // 短符号和短词：较大字号
        baseSize = 45 + Math.random() * 45;
      } else if (textLength <= 6) {
        // 中等长度：中等字号
        baseSize = 35 + Math.random() * 30;
      } else {
        // 长词汇：较小字号
        baseSize = 28 + Math.random() * 20;
      }
      symbol.style.fontSize = `${baseSize}px`;

      // 集中在左侧：90%的符号放在左侧(0-55%)，10%放在右侧(55-100%)
      const isLeftSide = Math.random() < 0.9;
      if (isLeftSide) {
        symbol.style.left = `${Math.random() * 55}%`; // 0-55%
      } else {
        symbol.style.left = `${55 + Math.random() * 45}%`; // 55-100%
      }

      symbol.style.top = `${Math.random() * 100}%`; // 每次位置都不一样

      // 闪烁动画时长 - 更长，更慢
      const duration = 4 + Math.random() * 4;
      symbol.style.animationDuration = `${duration}s`;

      symbolsContainer.appendChild(symbol);

      // 动画结束后移除符号
      setTimeout(() => {
        symbol.remove();
      }, duration * 1000);
    };

    // 初始创建一些符号
    for (let i = 0; i < 8; i++) {
      setTimeout(createSymbol, i * 600);
    }

    // 每隔一段时间创建新符号 - 间隔更长
    const interval = setInterval(createSymbol, 800);

    return () => {
      clearInterval(interval);
      symbolsContainer.innerHTML = '';
    };
  }, []);

  // 入场动画
  useEffect(() => {
    if (!loginBoxRef.current || !starCalendarRef.current) return;

    // 登录框入场动画
    animate(loginBoxRef.current, {
      opacity: [0, 1],
      translateY: [50, 0],
      scale: [0.8, 1],
      duration: 1500,
      ease: 'outElastic(1, 0.8)',
      delay: 500,
    });

    // 星历计时器入场动画
    animate(starCalendarRef.current, {
      opacity: [0, 1],
      translateX: [50, 0],
      scale: [0.8, 1],
      duration: 1500,
      ease: 'outElastic(1, 0.8)',
      delay: 700,
    });

    // HUD角落动画
    animate(`.${styles.hudCorner}`, {
      opacity: [0, 0.5],
      scale: [0.5, 1],
      duration: 1000,
      delay: stagger(200),
      ease: 'outQuad',
    });

    // 状态点闪烁动画
    animate(`.${styles.statusDot}`, {
      scale: [0, 1],
      opacity: [0, 1],
      duration: 800,
      delay: stagger(100, { start: 1000 }),
      ease: 'outBack',
    });
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // 输入动画
    animate(e.target, {
      scale: [0.98, 1],
      duration: 200,
      ease: 'outQuad',
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (loading) return;

    // 提交按钮动画
    const button = e.currentTarget.querySelector('button');
    if (button) {
      animate(button, {
        scale: [1, 0.95, 1],
        duration: 300,
        ease: 'inOutQuad',
      });
    }

    try {
      setLoading(true);

      // 调用鉴权API登录
      const loginResult = await authLogin({
        identifier: formData.username,
        password: formData.password,
      });

      if (loginResult.success && loginResult.data) {
        const { user, needChangePassword } = loginResult.data;

        // 获取用户权限
        let permissions: PermissionInfo[] = [];
        try {
          const permResult = await authGetUserPermissions(user.id);
          if (permResult.success && Array.isArray(permResult.data?.permissions)) {
            permissions = permResult.data.permissions;
          }
        } catch (error) {
          console.warn('获取用户权限失败:', error);
        }

        // 校验平台访问权限
        const hasAccess = permissions.some((p) => p.id === 'P00006-M001');
        if (!hasAccess) {
          message.error('您没有访问 AdenCockpit 的权限，请联系管理员');
          setLoading(false);
          return;
        }

        // 保存用户信息和权限到store
        login(user, permissions);

        message.success('登录成功！');

        // 如果需要修改密码，提示用户
        if (needChangePassword) {
          message.warning('请尽快修改初始密码', 5);
        }

        // 退出动画
        if (loginBoxRef.current) {
          animate(loginBoxRef.current, {
            opacity: [1, 0],
            scale: [1, 0.8],
            duration: 500,
            ease: 'inBack',
            onComplete: () => {
              navigate('/dashboard');
            },
          });
        } else {
          navigate('/dashboard');
        }
      }
    } catch (error: any) {
      console.error('登录失败:', error);
      // 错误信息已在 request 拦截器中处理
      setLoading(false);
    }
  };

  return (
    <div className={styles.loginContainer}>
      {/* 星空背景 */}
      <div ref={starfieldRef} className={styles.starfield} />

      {/* 明亮恒星 */}
      <div className={styles.brightStar} />

      {/* 星云 */}
      <div className={styles.nebula} />

      {/* 网格背景 */}
      <div className={styles.gridBackground} />

      {/* 数学物理符号动画 */}
      <div ref={symbolsRef} className={styles.symbolsLayer} />

      {/* 鼠标移动粒子效果 */}
      <div ref={particlesRef} className={styles.particlesContainer} />

      {/* HUD 装饰元素 */}
      <div className={styles.hudLines}>
        <div className={`${styles.hudCorner} ${styles.topLeft}`} />
        <div className={`${styles.hudCorner} ${styles.topRight}`} />
        <div className={`${styles.hudCorner} ${styles.bottomLeft}`} />
        <div className={`${styles.hudCorner} ${styles.bottomRight}`} />
      </div>

      {/* 登录表单 */}
      <div ref={loginBoxRef} className={styles.loginBox}>
        {/* 伊敦星历计时器 */}
        <div ref={starCalendarRef} className={styles.starCalendar}>
          <div className={styles.starTime}>{starTime}</div>
        </div>
        <div className={styles.logoSection}>
          <h1 className={styles.logoTitle}>AdenCockpit</h1>
          <p className={styles.logoSubtitle}>驾驶舱系统</p>
          <div className={styles.statusBar}>
            <div className={styles.statusItem}>
              <div className={styles.statusDot} />
              <span>SYSTEM ONLINE</span>
            </div>
            <div className={styles.statusItem}>
              <div className={styles.statusDot} />
              <span>SECURED</span>
            </div>
            <div className={styles.statusItem}>
              <div className={styles.statusDot} />
              <span>READY</span>
            </div>
          </div>
        </div>

        <form className={styles.loginForm} onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Aden ID</label>
            <div className={styles.inputWrapper}>
              <input
                type="text"
                name="username"
                className={styles.formInput}
                placeholder="输入Aden ID"
                value={formData.username}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Aden Key</label>
            <div className={styles.inputWrapper}>
              <input
                type="password"
                name="password"
                className={styles.formInput}
                placeholder="输入Aden Key"
                value={formData.password}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          <button type="submit" className={styles.loginButton} disabled={loading}>
            {loading ? '登录中...' : '登录舰桥'}
          </button>
        </form>

        <div className={styles.footer}>
          <div className={styles.systemInfo}>
            <div className={styles.infoItem}>
              <ThunderboltOutlined style={{ fontSize: '14px' }} />
              <span>POWER: 100%</span>
            </div>
            <div className={styles.infoItem}>
              <SafetyOutlined style={{ fontSize: '14px' }} />
              <span>SHIELD: ACTIVE</span>
            </div>
            <div className={styles.infoItem}>
              <ApiOutlined style={{ fontSize: '14px' }} />
              <span>SIGNAL: STRONG</span>
            </div>
          </div>
          <p style={{ marginTop: '15px', opacity: 0.5 }}>
            © 2024 AdenCockpit Systems • v1.0.0
          </p>
        </div>
      </div>
    </div>
  );
}

export default CockpitLogin;
